import React, {
  useEffect,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  useSelector,
  useDispatch,
} from "react-redux";

import {
  Link,
} from "react-router-dom";

import {
  motion,
} from "framer-motion";

import {
  FeaturedMealsCard,
  Loader,
} from "../utils";

import {
  getAllFeaturedMeals,
} from "../../redux/actions/featuredMeals";

import {
  useStateContext,
} from "../../context/ContextProvider";

import {
  FiArrowLeft,
  FiArrowRight,
} from "react-icons/fi";

import {
  FaFireAlt,
} from "react-icons/fa";


function FeaturedMealSections() {
  const {
    t,
  } = useTranslation();

  const dispatch =
    useDispatch();

  const {
    currentDir,
  } = useStateContext();


  /* =====================================
      DIRECTION
  ====================================== */

  const isRTL =
    currentDir === "rtl";


  const DirectionArrow =
    isRTL
      ? FiArrowLeft
      : FiArrowRight;


  /* =====================================
      FEATURED MEALS
  ====================================== */

  const {
    isLoading,
    Allmeals,
  } = useSelector(
    (state) =>
      state.FeaturedMeals
  );


  /* =====================================
      GLOBAL MOBILE CARD VIEW

      one = عمود واحد
      two = عمودان

      الافتراضي = عمودان
  ====================================== */

  const mobileMealColumns =
    useSelector(
      (state) =>
        state.Layout
          ?.mobileMealColumns ||
        "two"
    );


  /* =====================================
      MOBILE DETECTION
  ====================================== */

  const [
    isMobile,
    setIsMobile,
  ] = useState(() => {
    if (
      typeof window ===
      "undefined"
    ) {
      return false;
    }

    return (
      window.innerWidth <
      768
    );
  });


  useEffect(() => {
    const handleResize =
      () => {
        setIsMobile(
          window.innerWidth <
            768
        );
      };


    window.addEventListener(
      "resize",
      handleResize
    );


    handleResize();


    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );
    };
  }, []);


  /* =====================================
      LOAD FEATURED MEALS
  ====================================== */

  useEffect(() => {
    dispatch(
      getAllFeaturedMeals()
    );
  }, [
    dispatch,
  ]);


  /* =====================================
      SAFE DATA
  ====================================== */

  const featuredMeals =
    Array.isArray(
      Allmeals
    )
      ? Allmeals
      : [];


  /* =====================================
      CARD MODE

      compact فقط عندما:
      الهاتف + عمودان

      Desktop دائماً الشكل الكامل.
  ====================================== */

  const compactCard =
    isMobile &&
    mobileMealColumns ===
      "two";


  return (
    <section
      className="
        relative

        overflow-hidden

        bg-color-2

        py-16
        md:py-24
        lg:py-28
        xl:py-32
      "
    >

      {/* =====================================
          BACKGROUND DECORATION
      ====================================== */}

      <div
        className="
          absolute

          -top-44
          -end-44

          w-[460px]
          h-[460px]

          rounded-full

          border
          border-white/10

          pointer-events-none
        "
      />


      <div
        className="
          absolute

          top-16
          end-12

          w-[300px]
          h-[300px]

          rounded-full

          bg-color-1/15

          blur-[120px]

          pointer-events-none
        "
      />


      <div
        className="
          absolute

          -bottom-40
          -start-40

          w-[380px]
          h-[380px]

          rounded-full

          border
          border-color-1/10

          pointer-events-none
        "
      />


      {/* Subtle glow */}

      <div
        className="
          absolute

          top-1/2
          start-1/2

          w-[500px]
          h-[500px]

          -translate-x-1/2
          -translate-y-1/2

          rounded-full

          bg-color-1/[0.035]

          blur-[150px]

          pointer-events-none
        "
      />


      <div
        className="
          container

          relative
          z-10
        "
      >

        {/* =====================================
            SECTION HEADER
        ====================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 30,
          }}

          whileInView={{
            opacity: 1,
            y: 0,
          }}

          transition={{
            duration: 0.7,
          }}

          viewport={{
            once: true,
            amount: 0.2,
          }}

          className="
            grid

            grid-cols-1

            lg:grid-cols-[1.15fr_0.85fr]

            gap-6
            md:gap-8
            lg:gap-16

            items-end

            mb-9
            md:mb-14
            lg:mb-16
          "
        >

          {/* =================================
              TITLE SIDE
          ================================= */}

          <div>

            {/* Eyebrow */}

            <div
              className="
                inline-flex

                items-center

                gap-2.5
                md:gap-3

                mb-4
                md:mb-5
              "
            >

              <span
                className="
                  w-10
                  md:w-12

                  h-[2px]

                  bg-color-1
                "
              />


              <FaFireAlt
                className="
                  text-color-1

                  text-xs
                  md:text-sm
                "
              />


              <span
                className="
                  font-kufi

                  text-[10px]
                  sm:text-xs
                  md:text-sm

                  text-color-1
                "
              >
                {t(
                  "Yaman featured selections"
                )}
              </span>

            </div>


            {/* Main Title */}

            <h2
              className="
                max-w-3xl

                font-kufi
                font-bold

                text-white

                text-[1.8rem]
                leading-[1.6]

                sm:text-3xl

                md:text-5xl
                md:leading-[1.45]

                lg:text-[3.6rem]
                lg:leading-[1.4]
              "
            >
              {t(
                "special choices from Yaman"
              )}
            </h2>

          </div>


          {/* =================================
              DESCRIPTION SIDE
          ================================= */}

          <div>

            <p
              className="
                max-w-xl

                font-kufi

                text-[11px]
                sm:text-sm
                md:text-base

                leading-7
                md:leading-8

                text-white/60
              "
            >
              {t(
                "Yaman featured meals description"
              )}
            </p>

          </div>

        </motion.div>


        {/* =====================================
            DIVIDER
        ====================================== */}

        <div
          className="
            relative

            w-full
            h-px

            bg-white/10

            mb-7
            md:mb-10
          "
        >

          <div
            className="
              absolute

              start-0
              top-0

              w-16
              md:w-24

              h-px

              bg-color-1
            "
          />

        </div>


        {/* =====================================
            CARDS
        ====================================== */}

        {isLoading &&
        featuredMeals.length ===
          0 ? (

          <div
            className="
              min-h-[300px]

              flex
              items-center
              justify-center
            "
          >
            <Loader />
          </div>

        ) : (

          <motion.div
            layout

            className={`
              grid

              ${
                mobileMealColumns ===
                "two"
                  ? `
                    grid-cols-2
                    gap-3
                  `
                  : `
                    grid-cols-1
                    gap-6
                  `
              }

              md:grid-cols-2
              md:gap-7

              xl:grid-cols-3
              xl:gap-8
            `}
          >

            {featuredMeals.map(
              (
                meal,
                index
              ) => (

                <motion.div
                  layout

                  key={
                    meal?._id ||
                    index
                  }

                  className="
                    min-w-0
                    h-full
                  "
                >

                  <FeaturedMealsCard
                    meals={
                      meal
                    }

                    index={
                      index
                    }

                    compact={
                      compactCard
                    }
                  />

                </motion.div>

              )
            )}

          </motion.div>

        )}


        {/* =====================================
            EMPTY STATE
        ====================================== */}

        {!isLoading &&
          featuredMeals.length ===
            0 && (

          <div
            className="
              py-14

              text-center
            "
          >

            <div
              className="
                flex

                w-14
                h-14

                mx-auto
                mb-4

                items-center
                justify-center

                rounded-full

                border
                border-color-1/20

                bg-color-1/10

                text-color-1
              "
            >
              <FaFireAlt
                size={20}
              />
            </div>


            <p
              className="
                font-kufi

                text-sm

                text-white/45
              "
            >
              {t(
                "no offers found"
              )}
            </p>

          </div>

        )}


        {/* =====================================
            VIEW ALL
        ====================================== */}

        {featuredMeals.length >
          0 && (

          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}

            whileInView={{
              opacity: 1,
              y: 0,
            }}

            transition={{
              duration: 0.6,
            }}

            viewport={{
              once: true,
            }}

            className="
              flex

              justify-center

              mt-10
              md:mt-14
              lg:mt-16
            "
          >

            <Link
              to="/AllFeaturedMeals"

              onClick={() =>
                window.scrollTo({
                  top: 0,
                  behavior:
                    "smooth",
                })
              }

              className="
                group

                relative

                inline-flex

                min-h-[50px]
                md:min-h-[56px]

                items-center
                justify-center

                gap-3

                overflow-hidden

                rounded-full

                border
                border-color-1

                bg-color-1

                px-6
                md:px-8

                font-kufi
                font-medium

                text-xs
                md:text-sm
                lg:text-base

                text-white

                shadow-[0_14px_40px_rgba(165,119,58,0.22)]

                transition-all
                duration-300

                hover:bg-transparent
                hover:text-color-1

                hover:-translate-y-1
              "
            >

              {/* Hover decoration */}

              <span
                className="
                  absolute

                  -start-10

                  w-14
                  h-14

                  rounded-full

                  bg-white/10

                  transition-transform
                  duration-500

                  group-hover:scale-[7]
                "
              />


              <span
                className="
                  relative
                  z-10
                "
              >
                {t(
                  "view all Yaman selections"
                )}
              </span>


              <DirectionArrow
                className={`
                  relative
                  z-10

                  text-base
                  md:text-lg

                  transition-transform
                  duration-300

                  ${
                    isRTL
                      ? `
                        group-hover:-translate-x-1
                      `
                      : `
                        group-hover:translate-x-1
                      `
                  }
                `}
              />

            </Link>

          </motion.div>

        )}

      </div>

    </section>
  );
}


export default FeaturedMealSections;